import {generateInitialState} from "@/helpers/generateInitialState";
import {createSlice, isAnyOf} from "@reduxjs/toolkit";
import {blogsInitialState} from "../initialStates";
import {loadBlogs, loadBlogsCategories, loadForYouBlogs, loadSavedPosts, savePost, searchPost} from "./blogsActions";
import {uniqBy} from "es-toolkit/compat";

const initialState = await generateInitialState("blogs", blogsInitialState);

const blogsSlice = createSlice({
  name: "blogs",
  initialState,
  reducers: {
    clearForYouBlogs: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data.forYouPosts.results = [];
      state.data.forYouPosts.meta = {};
    },
    updateBlogsCategoriesState: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.categories = action.payload;
    },
    updateBlogState: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data = action.payload;
    },
    updateLatestPostState: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data.latestPosts.results = uniqBy(
        [...(state.data?.latestPosts?.results || []), ...(action.payload?.data || [])],
        "id",
      );
      state.data.latestPosts.meta = action.payload?.meta || {};
    },
    updateForYouPostState: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data.forYouPosts.results = uniqBy(
        [...(state.data?.forYouPosts?.results || []), ...(action.payload?.data || [])],
        "id",
      );
      state.data.forYouPosts.meta = action.payload?.meta || {};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadBlogs.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.data.latestPosts.results = uniqBy(
          [...(state.data?.latestPosts?.results || []), ...action.payload.data],
          "id",
        );
        state.data.latestPosts.meta = action.payload.meta;
      })
      .addCase(loadForYouBlogs.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.data.forYouPosts.results = uniqBy(
          [...(state.data?.forYouPosts?.results || []), ...action.payload.data],
          "id",
        );
        state.data.forYouPosts.meta = action.payload.meta;
      })
      .addCase(loadSavedPosts.fulfilled, (state, action) => {
        const listOfPosts = action.payload.data || [];

        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.data.savedPosts = uniqBy([...(state.data?.savedPosts || []), ...listOfPosts], "id");
      })
      .addCase(loadBlogsCategories.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.categories = action.payload;
      })
      .addMatcher(
        isAnyOf(
          loadBlogs.pending,
          loadForYouBlogs.pending,
          loadSavedPosts.pending,
          loadBlogsCategories.pending,
          savePost.pending,
          searchPost.pending,
        ),
        (state, action) => {
          state.isFetching = true;
          state.hasFailed = false;
          state.errorMsg = "";
        },
      )
      .addMatcher(
        isAnyOf(
          loadBlogs.rejected,
          loadForYouBlogs.rejected,
          loadSavedPosts.rejected,
          loadBlogsCategories.rejected,
          savePost.rejected,
          searchPost.rejected,
        ),
        (state, action) => {
          const {message = "Something went wrong"} = action.payload || {};

          state.isFetching = false;
          state.hasFailed = true;
          state.errorMsg = message;
        },
      );
  },
  selectors: {
    blogsDataSelector: state => state.data,
    blogsCategoriesSelector: state => state.categories,
    blogsStatusSelector: state => ({
      isFetching: state?.isFetching || false,
      hasFailed: state?.hasFailed || false,
      errorMsg: state?.errorMsg || "",
    }),
  },
});

export const {
  clearForYouBlogs,
  updateBlogState,
  updateBlogsCategoriesState,
  updateForYouPostState,
  updateLatestPostState,
} = blogsSlice.actions;
export const {blogsDataSelector, blogsCategoriesSelector, blogsStatusSelector} = blogsSlice.selectors;
export default blogsSlice.reducer;
